import { usePathname } from 'next/navigation';
import { useRouter } from 'next/navigation';
import { useCallback, useMemo, useState } from 'react';
import { useDisclosure } from '~/app/_hooks/use-disclosure';
import {
  buildOpenAppClickedParams,
  sendToFirebaseAnalytics,
} from '~/common/hooks/use-firebase-analytics';
import { routes } from '~/utils/routes';
import { createAppInstallUrl, createOpenEpisodeDeeplink } from '~/utils/url';
import { useLike } from './use-like';

type Params = {
  storyId: string;
  likeCount: number;
};

export const useLocalState = ({ storyId, likeCount }: Params) => {
  const router = useRouter();
  const [currentLikeCount, setCurrentLikeCount] = useState(likeCount);
  const sheetModalDisclosure = useDisclosure();
  const [sheetDescription, setSheetDescription] = useState<string | undefined>(
    undefined
  );
  const [disableLogin, setDisableLogin] = useState(false);
  const currentPath = usePathname();

  const handleOnLikeSuccess = useCallback(() => {
    const nextLikeCount = currentLikeCount + 1;
    setCurrentLikeCount(nextLikeCount);
  }, [currentLikeCount]);

  const { sendLike, enableLike } = useLike({
    storyId,
    onSuccess: handleOnLikeSuccess,
    onShowShouldLogin() {
      setSheetDescription(
        'もっといいねを行うにはアプリを使用するかログインしてください'
      );
      sheetModalDisclosure.onOpen();
    },
    onShowShouldInstallApp: () => {
      setDisableLogin(true);
      setSheetDescription('もっといいねを行うにはアプリを使用してください');
      sheetModalDisclosure.onOpen();
    },
  });

  const appLink = useMemo(() => {
    return createAppInstallUrl({
      deepLinkValue: createOpenEpisodeDeeplink(storyId),
    });
  }, [storyId]);

  const onOpenAppClick = useCallback(() => {
    if (typeof window === 'undefined') return;
    const eventPage = location.pathname;
    const eventParentPage = location.pathname.replace(/\/[^/]*$/, '');

    sendToFirebaseAnalytics(
      'open_app_clicked',
      buildOpenAppClickedParams({
        page: eventPage,
        parentPage: eventParentPage,
        component: 'story_like',
      })
    );

    window.location.href = appLink;
  }, [appLink]);

  const onClickLogin = useCallback(() => {
    sheetModalDisclosure.onClose();
    router.push(routes.login.url(currentPath));
  }, [currentPath, router, sheetModalDisclosure]);

  const onClickLike = useCallback(async () => {
    await sendLike();
  }, [sendLike]);

  return {
    enableLike,
    currentLikeCount,
    sheetDescription,
    disableLogin,
    sheetModalDisclosure,
    onClickLike,
    onClickLogin,
    onOpenAppClick,
  };
};
